import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Type, SmilePlus, PenLine, Wand2, Music, BarChart3, Scissors,
  Trash2, RotateCw, Minus, Plus, AtSign, Timer, MessageCircleQuestion, SlidersHorizontal, Check, ChevronUp,
  Image as ImageIcon,
} from "lucide-react";

const LAYER_LABEL = {
  text: "Testo", emoji: "Emoji", sticker: "Adesivo", mention: "@Menzione",
  location: "Luogo", countdown: "Countdown", music: "Musica",
  poll: "Sondaggio", question: "Domanda", slider: "Slider",
};
import { toast } from "sonner";
import { api } from "@/App";
import { MUSIC_LIBRARY, getMusicPlayer } from "../MusicLibrary";
import StoryOverlayLayer from "./StoryOverlayLayer";
import TextComposer from "./TextComposer";
import {
  FONTS, TEXT_STYLES, PALETTE, FILTERS, EMOJI_PACK, ADE_STICKER_PACK,
  DRAW_COLORS, DRAW_WIDTHS, MAX_STORY_SECONDS, newId, filterCss,
} from "./storyOverlays";
import { fxLike, fxReward } from "@/utils/gamefx";
import { uploadVideoChunks, waitForJob } from "@/utils/videoUpload";
import { createFrameGrabber } from "./frameGrab";
import "./storyStudio.css";

export default function StoryStudio({ file, kind, onClose, onPublished }) {
  const [mediaUrl, setMediaUrl] = useState(null);
  const isVideo = kind === "video";

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setMediaUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const [elements, setElements] = useState([]);
  const [strokes, setStrokes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [panel, setPanel] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState(DRAW_COLORS[1]);
  const [drawWidth, setDrawWidth] = useState(DRAW_WIDTHS[1]);
  const [drawGlow, setDrawGlow] = useState(true);
  const [filter, setFilter] = useState("ade");
  const [vignette, setVignette] = useState(true);
  const [grain, setGrain] = useState(false);
  const [music, setMusic] = useState("none");
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [clipMode, setClipMode] = useState(true);
  const [clipThumbs, setClipThumbs] = useState([]);
  const [clipCovers, setClipCovers] = useState([]);
  const [coverEdit, setCoverEdit] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const grabberRef = useRef(null);

  const [draft, setDraft] = useState(null); // form aperta nel pannello
  const [textEdit, setTextEdit] = useState(null); // compositore testo inline stile IG
  const [connections, setConnections] = useState([]);

  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const musicRef = useRef(getMusicPlayer());
  const strokeRef = useRef(null);

  useEffect(() => { const player = musicRef.current; return () => player.stop(); }, []);

  useEffect(() => {
    api.get("/connections").then((r) => setConnections(r.data.connections || [])).catch(() => {});
  }, []);

  const overlays = useMemo(() => ({
    version: 1,
    filter: filterCss(filter),
    vignette,
    grain,
    strokes,
    elements,
  }), [filter, vignette, grain, strokes, elements]);

  const addElement = (el) => {
    // posizioni distribuite: i nuovi elementi non si sovrappongono
    const n = elements.length;
    const slots = [
      [0.5, 0.5], [0.5, 0.26], [0.5, 0.74], [0.3, 0.38],
      [0.7, 0.62], [0.3, 0.62], [0.7, 0.38], [0.5, 0.14],
    ];
    const [sx, sy] = slots[n % slots.length];
    const withDefaults = { id: newId(el.type), x: sx, y: sy, scale: 1, rot: 0, ...el };
    setElements((prev) => [...prev, withDefaults]);
    setSelectedId(withDefaults.id);
    setPanel(null);
    setDraft(null);
    fxLike();
  };

  const updateSelected = (patch) => {
    setElements((prev) => prev.map((el) => (el.id === selectedId ? { ...el, ...patch } : el)));
  };

  const selected = elements.find((el) => el.id === selectedId) || null;

  const onDragElement = useCallback((id, x, y) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, x, y } : el)));
  }, []);

  const onTransformElement = useCallback((id, patch) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  }, []);

  // tap pulito su un testo → riapri il compositore per modificarlo
  const onTapElement = (id) => {
    const el = elements.find((x) => x.id === id);
    if (el?.type !== "text") return;
    setTextEdit({
      id: el.id, text: el.text || "", font: el.font || "display",
      style: el.style || "plain", color: el.color || "#FFFFFF",
      bgColor: el.bgColor || el.color || "#FFFFFF",
    });
  };

  // tap secco su area vuota → scrivi subito (come IG)
  const tapRef = useRef(null);
  const onStagePointerDown = (e) => {
    if (drawing) { drawPointer(e); return; }
    tapRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), had: !!selectedId || !!panel };
    setSelectedId(null);
  };
  const onStagePointerUp = (e) => {
    if (drawing) { drawPointer(e); return; }
    const tp = tapRef.current;
    tapRef.current = null;
    if (!tp || tp.had || gest.current) return;
    if (Date.now() - tp.t > 350 || Math.hypot(e.clientX - tp.x, e.clientY - tp.y) > 10) return;
    setTextEdit({ id: null, text: "", font: "display", style: "plain", color: "#FFFFFF", bgColor: "#FFFFFF" });
  };

  // ---- disegno ----
  const drawPointer = (e) => {
    if (!drawing) return;
    const rect = stageRef.current.getBoundingClientRect();
    const pt = [
      Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    ];
    if (e.type === "pointerdown") {
      strokeRef.current = { color: drawColor, width: drawWidth, glow: drawGlow, points: [pt] };
      setStrokes((prev) => [...prev, strokeRef.current]);
    } else if (e.type === "pointermove" && strokeRef.current) {
      strokeRef.current.points.push(pt);
      setStrokes((prev) => [...prev.slice(0, -1), { ...strokeRef.current }]);
    } else {
      strokeRef.current = null;
    }
  };

  // ---- inquadratura: pinch per zoomare/ruotare, trascina per spostare (come IG) ----
  const [frame, setFrame] = useState({ scale: 1, x: 0, y: 0, rot: 0 });
  const gest = useRef(null);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const angle = (t) => Math.atan2(t[1].clientY - t[0].clientY, t[1].clientX - t[0].clientX) * 180 / Math.PI;
  const mid = (t) => [(t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2];

  // iOS Safari: blocca lo zoom/gesture della pagina, lascia i tocchi al canvas
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const prevent = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
    const preventAll = (e) => e.preventDefault();
    el.addEventListener("touchmove", prevent, { passive: false });
    el.addEventListener("gesturestart", preventAll);
    el.addEventListener("gesturechange", preventAll);
    return () => {
      el.removeEventListener("touchmove", prevent);
      el.removeEventListener("gesturestart", preventAll);
      el.removeEventListener("gesturechange", preventAll);
    };
  }, []);

  const onFrameTouchStart = (e) => {
    if (drawing || selectedId || isVideo) return;
    const t = Array.from(e.touches);
    if (t.length === 2) {
      tapRef.current = null;
      const [mx, my] = mid(t);
      gest.current = {
        mode: "pinch", d: dist(t), scale: frame.scale,
        a: angle(t), rot: frame.rot,
        mx, my, ox: frame.x, oy: frame.y,
      };
    } else if (t.length === 1 && frame.scale > 1.01) {
      gest.current = { mode: "pan", x: t[0].clientX, y: t[0].clientY, ox: frame.x, oy: frame.y };
    }
  };

  const onFrameTouchMove = (e) => {
    const g = gest.current;
    if (!g) return;
    const t = Array.from(e.touches);
    if (g.mode === "pinch" && t.length === 2) {
      const scale = Math.min(4, Math.max(1, g.scale * (dist(t) / g.d)));
      let rot = g.rot;
      if (!isVideo) {
        rot = g.rot + (angle(t) - g.a);
        const snap = Math.round(rot / 90) * 90;
        if (Math.abs(rot - snap) < 4) rot = snap;
      }
      // due dita: sposta anche l'inquadratura seguendo il punto medio
      const [mx, my] = mid(t);
      const w = stageRef.current?.clientWidth || 1;
      const h = stageRef.current?.clientHeight || 1;
      const lim = rot ? 60 : (scale - 1) * 50;
      const x = Math.max(-lim, Math.min(lim, g.ox + ((mx - g.mx) / w) * 100));
      const y = Math.max(-lim, Math.min(lim, g.oy + ((my - g.my) / h) * 100));
      setFrame({ scale, rot, x, y });
    } else if (g.mode === "pan" && t.length === 1) {
      const lim = (frame.scale - 1) * 50 + (frame.rot ? 10 : 0);
      setFrame((f) => ({
        ...f,
        x: Math.max(-lim, Math.min(lim, g.ox + ((t[0].clientX - g.x) / (stageRef.current?.clientWidth || 1)) * 100)),
        y: Math.max(-lim, Math.min(lim, g.oy + ((t[0].clientY - g.y) / (stageRef.current?.clientHeight || 1)) * 100)),
      }));
    }
  };

  const onFrameTouchEnd = (e) => {
    if (e && e.touches && e.touches.length === 1 && gest.current?.mode === "pinch") {
      // da due dita a una: continua col pan senza scatti
      const t = e.touches[0];
      gest.current = { mode: "pan", x: t.clientX, y: t.clientY, ox: frame.x, oy: frame.y };
      return;
    }
    gest.current = null;
    // scala ~1 e rotazione ~0: riaggancia pulito
    setFrame((f) => ({
      ...f,
      scale: f.scale < 1.03 ? 1 : f.scale,
      x: f.scale < 1.03 && !f.rot ? 0 : f.x,
      y: f.scale < 1.03 && !f.rot ? 0 : f.y,
    }));
  };

  const frameStyle = {
    transform: `translate(${frame.x}%, ${frame.y}%) rotate(${frame.rot}deg) scale(${frame.scale})`,
    transformOrigin: "50% 50%",
  };

  // rende l'inquadratura definitiva su tela 1080x1920 (immagini)
  const bakeImage = () =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const W = 1080, H = 1920;
        const cv = document.createElement("canvas");
        cv.width = W; cv.height = H;
        const c = cv.getContext("2d");
        c.fillStyle = "#000";
        c.fillRect(0, 0, W, H);
        const cover = Math.max(W / img.width, H / img.height) * frame.scale;
        const w = img.width * cover, h = img.height * cover;
        c.filter = filterCss(filter) || "none";
        c.translate(W / 2 + (frame.x / 100) * W, H / 2 + (frame.y / 100) * H);
        c.rotate((frame.rot || 0) * Math.PI / 180);
        c.drawImage(img, -w / 2, -h / 2, w, h);
        cv.toBlob((b) => resolve(b || file), "image/jpeg", 0.92);
      };
      img.onerror = () => resolve(file);
      img.src = mediaUrl;
    });

  // ---- video lunghi: anteprima delle clip sequenziali ----
  const clipCount = useMemo(() => {
    if (!isVideo || duration <= MAX_STORY_SECONDS) return 1;
    return Math.min(10, Math.ceil(duration / MAX_STORY_SECONDS));
  }, [isVideo, duration]);

  useEffect(() => {
    if (!isVideo || !mediaUrl) { grabberRef.current = null; return; }
    const g = createFrameGrabber(mediaUrl);
    grabberRef.current = g;
    return () => { g.destroy(); grabberRef.current = null; };
  }, [isVideo, mediaUrl]);

  const clipLen = useCallback((i) => Math.max(1, Math.min(MAX_STORY_SECONDS, duration - i * MAX_STORY_SECONDS)), [duration]);

  useEffect(() => {
    if (!isVideo || !mediaUrl || clipCount < 2) { setClipThumbs([]); setClipCovers([]); return; }
    setClipCovers(Array.from({ length: clipCount }, () => 0.4));
    let cancelled = false;
    (async () => {
      const g = grabberRef.current;
      if (!g) return;
      const shots = [];
      for (let i = 0; i < clipCount; i++) {
        if (cancelled) return;
        shots.push(await g.grab(Math.min(duration - 0.2, i * MAX_STORY_SECONDS + 0.4)));
      }
      if (!cancelled) setClipThumbs(shots);
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [isVideo, mediaUrl, duration, clipCount]);

  // anteprima della copertina mentre si scorre il fotogramma
  useEffect(() => {
    if (coverEdit === null) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const g = grabberRef.current;
      if (!g) return;
      const at = coverEdit * MAX_STORY_SECONDS + (clipCovers[coverEdit] ?? 0.4);
      const shot = await g.grab(Math.min(duration - 0.1, at), 216, 384);
      if (!cancelled) setCoverPreview(shot);
    }, 140);
    return () => { cancelled = true; clearTimeout(t); };
  }, [coverEdit, clipCovers, duration]);

  const confirmCover = async () => {
    if (coverEdit === null) return;
    const g = grabberRef.current;
    const at = Math.min(duration - 0.1, coverEdit * MAX_STORY_SECONDS + (clipCovers[coverEdit] ?? 0.4));
    const thumb = g ? await g.grab(at) : coverPreview;
    if (thumb) {
      setClipThumbs((prev) => {
        const next = [...prev];
        next[coverEdit] = thumb;
        return next;
      });
    }
    fxLike();
    setCoverEdit(null);
    setCoverPreview(null);
  };

  // ---- pubblicazione ----
  const publish = async () => {
    setPublishing(true);
    setProgress(0);
    setPhase("");
    const payloadOverlays = JSON.stringify(overlays);
    try {
      if (!isVideo) {
        const form = new FormData();
        const baked = await bakeImage();
        form.append("file", new File([baked], `story_${Date.now()}.jpg`, { type: "image/jpeg" }));
        form.append("overlays", payloadOverlays);
        if (music !== "none") form.append("music_id", music);
        await api.post("/stories", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 90)),
        });
        setProgress(100);
        fxReward();
        toast.success("Mood pubblicato!");
      } else {
        const split = clipMode && clipCount > 1;
        setPhase("Caricamento video");
        const uploadId = await uploadVideoChunks(file, {
          kind: "story",
          onProgress: (p) => setProgress(Math.round(p * 55)),
        });
        const { data } = await api.post("/stories/video/complete", {
          upload_id: uploadId,
          trim_start: trimStart,
          overlays: payloadOverlays,
          music_id: music !== "none" ? music : null,
          split,
          clip_seconds: MAX_STORY_SECONDS,
          covers: split ? clipCovers.slice(0, clipCount) : undefined,
        });
        const result = await waitForJob(data.job_id, (p, ph) => {
          setProgress(55 + Math.round(p * 45));
          if (ph) setPhase(ph);
        });
        setProgress(100);
        fxReward();
        toast.success(result.clips > 1
          ? `${result.clips} clip pubblicate in sequenza!`
          : "Mood pubblicato e compresso!");
      }
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore nella pubblicazione");
    } finally {
      setPublishing(false);
    }
  };

  const railTools = [
    { id: "text", icon: Type, label: "Testo" },
    { id: "stickers", icon: SmilePlus, label: "Adesivi" },
    { id: "interactive", icon: BarChart3, label: "Interattivi" },
    { id: "draw", icon: PenLine, label: "Disegna" },
    { id: "filters", icon: Wand2, label: "Filtri" },
    { id: "music", icon: Music, label: "Musica" },
    ...(isVideo ? [{ id: "trim", icon: Scissors, label: "Taglia" }] : []),
  ];

  return createPortal(
    <div className={`sst-root ${panel ? "sst-panel-open" : ""}`} data-testid="story-studio">
      <div className="sst-aurora" />

      {panel && <div className="sst-backdrop" onClick={() => setPanel(null)} data-testid="studio-panel-backdrop" />}

      <div className="sst-topbar">
        <button className="sst-icon-btn" onClick={onClose} data-testid="studio-close-btn"><X className="w-5 h-5" /></button>
        <div className="flex gap-2">
          {selected && (
            <>
              <button className="sst-icon-btn" onClick={() => updateSelected({ scale: Math.max(0.4, (selected.scale || 1) - 0.15) })} data-testid="studio-scale-down"><Minus className="w-4 h-4" /></button>
              <button className="sst-icon-btn" onClick={() => updateSelected({ scale: Math.min(3, (selected.scale || 1) + 0.15) })} data-testid="studio-scale-up"><Plus className="w-4 h-4" /></button>
              <button className="sst-icon-btn" onClick={() => updateSelected({ rot: ((selected.rot || 0) + 15) % 360 })} data-testid="studio-rotate"><RotateCw className="w-4 h-4" /></button>
              <button className="sst-icon-btn" onClick={() => { setElements((p) => p.filter((e) => e.id !== selectedId)); setSelectedId(null); }} data-testid="studio-delete"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      <div className="sst-rail">
        {railTools.map((t) => (
          <button
            key={t.id}
            className={`sst-icon-btn ${panel === t.id || (t.id === "draw" && drawing) ? "sst-on" : ""}`}
            onClick={() => {
              setSelectedId(null);
              if (t.id === "draw") { setDrawing((d) => !d); setPanel(panel === "draw" ? null : "draw"); return; }
              setDrawing(false);
              if (t.id === "text") {
                setPanel(null);
                setDraft(null);
                setTextEdit({ id: null, text: "", font: "display", style: "plain", color: "#FFFFFF", bgColor: "#FFFFFF" });
                return;
              }
              setPanel(panel === t.id ? null : t.id);
              setDraft(null);
            }}
            aria-label={t.label}
            data-testid={`studio-tool-${t.id}`}
          >
            <t.icon className="w-5 h-5" />
          </button>
        ))}
      </div>

      <div className="sst-stage-wrap">
        <div
          className="sst-stage"
          ref={stageRef}
          onPointerDown={onStagePointerDown}
          onPointerMove={drawPointer}
          onPointerUp={onStagePointerUp}
          onTouchStart={onFrameTouchStart}
          onTouchMove={onFrameTouchMove}
          onTouchEnd={onFrameTouchEnd}
          data-testid="studio-stage"
        >
          {isVideo ? (
            <video
              ref={videoRef}
              className="sst-media"
              src={mediaUrl || undefined}
              style={{ filter: filterCss(filter), ...frameStyle }}
              playsInline autoPlay loop muted
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            />
          ) : (
            <img className="sst-media" src={mediaUrl || undefined} alt="" style={{ filter: filterCss(filter), ...frameStyle }} draggable={false} data-testid="studio-media" />
          )}
          {!isVideo && frame.scale <= 1.01 && !frame.rot && !drawing && (
            <p className="sst-pinch-hint" data-testid="studio-pinch-hint">Pizzica per zoomare e ruotare · trascina per spostare</p>
          )}
          {vignette && <div className="sst-vignette" />}
          {grain && <div className="sst-grain" />}

          <StoryOverlayLayer
            overlays={overlays}
            mode="edit"
            selectedId={selectedId}
            onSelect={setSelectedId}
            onDragElement={onDragElement}
            onTransformElement={onTransformElement}
            onTapElement={onTapElement}
          />
        </div>
      </div>

      {isVideo && duration > MAX_STORY_SECONDS && !panel && (
        <div className="sst-chipbar">
          <button className="sst-chip" onClick={() => setPanel("trim")} data-testid="studio-clip-chip">
            <Scissors className="w-3.5 h-3.5" />
            {clipMode && clipCount > 1
              ? `${Math.round(duration)}s → ${clipCount} clip in sequenza`
              : `${Math.round(duration)}s → 30s dal secondo ${Math.round(trimStart)}`}
          </button>
        </div>
      )}

      {panel && (
        <div className="sst-panel" data-testid={`studio-panel-${panel}`}>
          {panel === "stickers" && (
            <>
              <p className="sst-panel-title">ADESIVI ADE</p>
              <div className="sst-grid">
                {ADE_STICKER_PACK.map((s) => (
                  <button key={s.id} className="sst-tile" onClick={() => addElement({ type: "sticker", sticker: s.id, label: s.label, kind: s.kind })}
                    data-testid={`studio-sticker-${s.id}`}>{s.label}</button>
                ))}
              </div>
              <p className="sst-panel-title mt-4">EMOJI</p>
              <div className="sst-grid">
                {EMOJI_PACK.map((c) => (
                  <button key={c} className="sst-tile sst-emoji-tile" onClick={() => addElement({ type: "emoji", char: c })}
                    data-testid={`studio-emoji-${c}`}>{c}</button>
                ))}
              </div>
              <p className="sst-panel-title mt-4">MENZIONA</p>
              <div className="sst-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
                {connections.slice(0, 9).map((u) => (
                  <button key={u.user_id} className="sst-tile" onClick={() => addElement({ type: "mention", user_id: u.user_id, name: u.name?.split(" ")[0] })}
                    data-testid={`studio-mention-${u.user_id}`}>
                    <AtSign className="w-3 h-3 inline mr-1" />{u.name?.split(" ")[0]}
                  </button>
                ))}
              </div>
              <p className="sst-panel-title mt-4">LUOGO / COUNTDOWN</p>
              <input className="sst-input" placeholder="Aggiungi un luogo e premi Invio"
                onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value.trim()) addElement({ type: "location", label: e.currentTarget.value.trim().slice(0, 40) }); }}
                data-testid="studio-location-input" />
              <button className="sst-cta" style={{ background: "linear-gradient(92deg,#4FD1FF,#7B2CFF)", color: "#fff" }}
                onClick={() => addElement({ type: "countdown", label: "MANCA POCO", target_iso: new Date(Date.now() + 86400000).toISOString() })}
                data-testid="studio-countdown-add">
                <Timer className="w-4 h-4 inline mr-1" /> Countdown 24h
              </button>
            </>
          )}

          {panel === "interactive" && (
            <>
              <p className="sst-panel-title">STICKER INTERATTIVI</p>
              {!draft && (
                <div className="sst-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
                  <button className="sst-tile" onClick={() => setDraft({ type: "poll", question: "", options: ["", ""] })} data-testid="studio-add-poll">
                    <BarChart3 className="w-4 h-4 mx-auto mb-1" />Sondaggio
                  </button>
                  <button className="sst-tile" onClick={() => setDraft({ type: "question", prompt: "" })} data-testid="studio-add-question">
                    <MessageCircleQuestion className="w-4 h-4 mx-auto mb-1" />Domanda
                  </button>
                  <button className="sst-tile" onClick={() => setDraft({ type: "slider", prompt: "", emoji: "🔥" })} data-testid="studio-add-slider">
                    <SlidersHorizontal className="w-4 h-4 mx-auto mb-1" />Slider
                  </button>
                </div>
              )}

              {draft?.type === "poll" && (
                <>
                  <input className="sst-input mb-2" placeholder="La tua domanda" value={draft.question}
                    onChange={(e) => setDraft({ ...draft, question: e.target.value.slice(0, 80) })} data-testid="studio-poll-question" />
                  {draft.options.map((o, i) => (
                    <input key={i} className="sst-input mb-2" placeholder={`Opzione ${i + 1}`} value={o}
                      onChange={(e) => setDraft({ ...draft, options: draft.options.map((v, j) => (j === i ? e.target.value.slice(0, 30) : v)) })}
                      data-testid={`studio-poll-option-${i}`} />
                  ))}
                  {draft.options.length < 4 && (
                    <button className="sst-tile w-full" onClick={() => setDraft({ ...draft, options: [...draft.options, ""] })}>+ Opzione</button>
                  )}
                  <button className="sst-cta" disabled={!draft.question.trim() || draft.options.filter((o) => o.trim()).length < 2}
                    onClick={() => addElement({ type: "poll", question: draft.question.trim(), options: draft.options.filter((o) => o.trim()) })}
                    data-testid="studio-poll-add">Aggiungi sondaggio</button>
                </>
              )}

              {draft?.type === "question" && (
                <>
                  <input className="sst-input" placeholder="Fai una domanda..." value={draft.prompt}
                    onChange={(e) => setDraft({ ...draft, prompt: e.target.value.slice(0, 80) })} data-testid="studio-question-prompt" />
                  <button className="sst-cta" disabled={!draft.prompt.trim()}
                    onClick={() => addElement({ type: "question", prompt: draft.prompt.trim() })} data-testid="studio-question-add">
                    Aggiungi domanda
                  </button>
                </>
              )}

              {draft?.type === "slider" && (
                <>
                  <input className="sst-input mb-2" placeholder="Quanto ti piace?" value={draft.prompt}
                    onChange={(e) => setDraft({ ...draft, prompt: e.target.value.slice(0, 60) })} data-testid="studio-slider-prompt" />
                  <div className="sst-row">
                    {["🔥", "❤️", "😍", "💎", "🚀", "🍸"].map((c) => (
                      <button key={c} className={`sst-tile sst-emoji-tile ${draft.emoji === c ? "sst-on" : ""}`}
                        onClick={() => setDraft({ ...draft, emoji: c })}>{c}</button>
                    ))}
                  </div>
                  <button className="sst-cta" disabled={!draft.prompt.trim()}
                    onClick={() => addElement({ type: "slider", prompt: draft.prompt.trim(), emoji: draft.emoji })}
                    data-testid="studio-slider-add">Aggiungi slider</button>
                </>
              )}
            </>
          )}

          {panel === "draw" && (
            <>
              <p className="sst-panel-title">PENNELLO NEON</p>
              <div className="sst-row">
                {DRAW_COLORS.map((c) => (
                  <button key={c} className={`sst-swatch ${drawColor === c ? "sst-on" : ""}`} style={{ background: c }}
                    onClick={() => setDrawColor(c)} aria-label={c} data-testid={`studio-draw-color-${c}`} />
                ))}
              </div>
              <div className="sst-row">
                {DRAW_WIDTHS.map((w) => (
                  <button key={w} className={`sst-tile ${drawWidth === w ? "sst-on" : ""}`} style={{ padding: "8px 16px" }}
                    onClick={() => setDrawWidth(w)}>{w}px</button>
                ))}
                <button className={`sst-tile ${drawGlow ? "sst-on" : ""}`} style={{ padding: "8px 16px" }}
                  onClick={() => setDrawGlow((g) => !g)}>Glow</button>
                <button className="sst-tile" style={{ padding: "8px 16px" }} onClick={() => setStrokes([])} data-testid="studio-draw-clear">Pulisci</button>
              </div>
              <p className="sst-hint">Disegna direttamente sulla storia. Tocca di nuovo l'icona per uscire.</p>
            </>
          )}

          {panel === "filters" && (
            <>
              <p className="sst-panel-title">FILTRI CINEMATOGRAFICI</p>
              <div className="sst-grid">
                {FILTERS.map((f) => (
                  <button key={f.id} className={`sst-tile ${filter === f.id ? "sst-on" : ""}`}
                    onClick={() => setFilter(f.id)} data-testid={`studio-filter-${f.id}`}>{f.label}</button>
                ))}
              </div>
              <div className="sst-row mt-3">
                <button className={`sst-tile ${vignette ? "sst-on" : ""}`} style={{ padding: "8px 16px" }}
                  onClick={() => setVignette((v) => !v)} data-testid="studio-vignette">Vignettatura</button>
                <button className={`sst-tile ${grain ? "sst-on" : ""}`} style={{ padding: "8px 16px" }}
                  onClick={() => setGrain((g) => !g)} data-testid="studio-grain">Grana film</button>
              </div>
            </>
          )}

          {panel === "music" && (
            <>
              <p className="sst-panel-title">MOOD SONORO</p>
              <div className="sst-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0,1fr))" }}>
                {MUSIC_LIBRARY.map((t) => (
                  <button key={t.id} className={`sst-tile ${music === t.id ? "sst-on" : ""}`}
                    onClick={() => {
                      setMusic(t.id);
                      if (t.id !== "none") {
                        musicRef.current.start(t.id);
                        setTimeout(() => musicRef.current.stop(), 3000);
                        if (!elements.some((e) => e.type === "music")) {
                          addElement({ type: "music", music_id: t.id, name: t.name, y: 0.12 });
                        } else {
                          setElements((prev) => prev.map((e) => (e.type === "music" ? { ...e, music_id: t.id, name: t.name } : e)));
                        }
                      } else {
                        musicRef.current.stop();
                        setElements((prev) => prev.filter((e) => e.type !== "music"));
                      }
                    }}
                    data-testid={`studio-music-${t.id}`}>
                    <span className="mr-1">{t.icon}</span>{t.name}
                  </button>
                ))}
              </div>
            </>
          )}

          {panel === "trim" && (
            <>
              <p className="sst-panel-title">{clipCount > 1 ? "VIDEO LUNGO · SCEGLI LA MODALITÀ" : "TAGLIA · MAX 30 SECONDI"}</p>

              {clipCount > 1 && (
                <div className="sst-seg" data-testid="studio-clip-mode">
                  <button className={`sst-seg-btn ${clipMode ? "sst-on" : ""}`} onClick={() => setClipMode(true)} data-testid="studio-mode-clips">
                    Dividi in {clipCount} clip
                  </button>
                  <button className={`sst-seg-btn ${!clipMode ? "sst-on" : ""}`} onClick={() => setClipMode(false)} data-testid="studio-mode-trim">
                    Solo 30s
                  </button>
                </div>
              )}

              {clipCount > 1 && clipMode ? (
                coverEdit !== null ? (
                  <div className="sst-cover" data-testid="studio-cover-editor">
                    <p className="sst-hint" style={{ marginBottom: 10 }}>
                      Copertina clip {coverEdit + 1}/{clipCount} · scegli il fotogramma
                    </p>
                    <div className="sst-cover-frame">
                      {coverPreview
                        ? <img src={coverPreview} alt="" data-testid="studio-cover-preview" />
                        : <span className="sst-clip-skel" />}
                      <span className="sst-cover-time">
                        +{(clipCovers[coverEdit] ?? 0).toFixed(1)}s
                      </span>
                    </div>
                    <input
                      type="range" min={0} max={Math.max(0.5, clipLen(coverEdit) - 0.2)} step={0.1}
                      value={clipCovers[coverEdit] ?? 0.4}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setClipCovers((prev) => {
                          const next = [...prev];
                          next[coverEdit] = v;
                          return next;
                        });
                      }}
                      className="sst-slider-input" style={{ width: "100%", accentColor: "#FFD166" }}
                      data-testid="studio-cover-range"
                    />
                    <div className="sst-cover-actions">
                      <button className="sst-cover-back" onClick={() => { setCoverEdit(null); setCoverPreview(null); }} data-testid="studio-cover-cancel">
                        Indietro
                      </button>
                      <button className="sst-cta" style={{ marginTop: 0 }} onClick={confirmCover} data-testid="studio-cover-confirm">
                        <Check className="w-4 h-4 inline mr-1" /> Usa questo fotogramma
                      </button>
                    </div>
                  </div>
                ) : (
                <>
                  <p className="sst-hint" style={{ marginBottom: 10 }}>
                    {Math.round(duration)}s → {clipCount} storie da 30s · tocca una clip per scegliere la copertina
                  </p>
                  <div className="sst-clips" data-testid="studio-clip-strip">
                    {Array.from({ length: clipCount }).map((_, i) => (
                      <button
                        key={i} className="sst-clip" data-testid={`studio-clip-${i + 1}`}
                        onClick={() => { setCoverPreview(clipThumbs[i] || null); setCoverEdit(i); }}
                      >
                        {clipThumbs[i]
                          ? <img src={clipThumbs[i]} alt="" />
                          : <span className="sst-clip-skel" />}
                        <span className="sst-clip-badge">{i + 1}/{clipCount}</span>
                        <span className="sst-clip-edit"><ImageIcon className="w-3 h-3" /></span>
                      </button>
                    ))}
                  </div>
                  <button className="sst-cta" onClick={() => setPanel(null)} data-testid="studio-trim-done">
                    <Check className="w-4 h-4 inline mr-1" /> Conferma clip
                  </button>
                </>
                )
              ) : (
                <>
                  <p className="sst-hint" style={{ marginBottom: 12 }}>
                    Durata originale {duration ? Math.round(duration) : "?"}s · verrà pubblicata la finestra di {Math.min(MAX_STORY_SECONDS, Math.round(duration || MAX_STORY_SECONDS))}s
                  </p>
                  <input
                    type="range" min={0} max={Math.max(0, Math.floor((duration || 0) - MAX_STORY_SECONDS))} step={1}
                    value={trimStart}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setTrimStart(v);
                      if (videoRef.current) videoRef.current.currentTime = v;
                    }}
                    className="sst-slider-input" style={{ width: "100%", accentColor: "#FF3B5C" }}
                    data-testid="studio-trim-range"
                  />
                  <p className="sst-hint">Inizio: {Math.round(trimStart)}s → fine: {Math.round(Math.min(trimStart + MAX_STORY_SECONDS, duration || MAX_STORY_SECONDS))}s</p>
                  <button className="sst-cta" onClick={() => setPanel(null)} data-testid="studio-trim-done">
                    <Check className="w-4 h-4 inline mr-1" /> Conferma taglio
                  </button>
                </>
              )}
            </>
          )}
          <button className="sst-panel-handle" onClick={() => setPanel(null)} data-testid="studio-panel-close">
            <ChevronUp className="w-4 h-4" /> CHIUDI
          </button>
        </div>
      )}

      {elements.length > 0 && !panel && (
        <div className="sst-layers" data-testid="studio-layers">
          {elements.map((el) => (
            <button
              key={el.id}
              className={`sst-layer-chip ${selectedId === el.id ? "sst-on" : ""}`}
              onClick={() => setSelectedId(el.id)}
              data-testid={`studio-layer-${el.id}`}
            >
              {LAYER_LABEL[el.type] || el.type}
            </button>
          ))}
        </div>
      )}

      <div className="sst-bottom">
        {publishing && <div className="sst-progress"><span style={{ width: `${progress}%` }} /></div>}
        <button className="sst-publish" onClick={publish} disabled={publishing} data-testid="studio-publish-btn">
          {publishing
            ? `${phase || "Caricamento"} ${progress}%`
            : (isVideo && clipMode && clipCount > 1 ? `Pubblica ${clipCount} clip` : "Pubblica Mood")}
        </button>
      </div>

      {textEdit && (
        <TextComposer
          initial={textEdit}
          onCancel={() => setTextEdit(null)}
          onDone={(d) => {
            const { id, ...vals } = d;
            if (id) {
              setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...vals } : el)));
            } else {
              addElement({ type: "text", ...vals, x: 0.5, y: 0.42 });
            }
            setTextEdit(null);
          }}
        />
      )}
    </div>,
    document.body
  );
}
